import { Button, Box, Typography, Chip } from "@mui/material";
import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useUser } from "../../contexts/UserContext";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";

const AZUL = "#0b243d";
const AMARELO = "#ffc845";

const cardSx = {
    backgroundColor: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "16px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    p: { xs: 2.5, md: 4 },
};

const botaoPrimarioSx = {
    textTransform: "none",
    fontSize: 15,
    fontWeight: 700,
    color: "#111827",
    backgroundColor: AMARELO,
    borderRadius: "10px",
    boxShadow: "0 6px 20px rgba(255, 200, 69, 0.35)",
    px: 3,
    py: 1.25,
    transition: "all 0.2s ease",
    "&:hover": { backgroundColor: "#f5b82e", boxShadow: "0 8px 24px rgba(255, 200, 69, 0.45)", transform: "translateY(-1px)" },
};

const botaoSecundarioEscuroSx = {
    textTransform: "none",
    fontSize: 15,
    fontWeight: 600,
    color: "#fff",
    backgroundColor: "rgba(255, 255, 255, 0.08)",
    border: "1px solid rgba(255, 255, 255, 0.16)",
    borderRadius: "10px",
    px: 3,
    py: 1.25,
    "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.14)" },
};

const aparecer = {
    hidden: { opacity: 0, y: 24 },
    visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" } }),
};

const Secao = ({ children, ...props }) => (
    <motion.div variants={aparecer} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} {...props}>
        {children}
    </motion.div>
);

const TituloSecao = ({ etiqueta, titulo, descricao }) => (
    <Box sx={{ textAlign: "center", maxWidth: 640, mx: "auto", mb: { xs: 3, md: 4 } }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", color: "#b45309" }}>{etiqueta}</Typography>
        <Typography sx={{ mt: 1, fontSize: { xs: 24, md: 30 }, fontWeight: 800, color: "#111827", lineHeight: 1.2 }}>{titulo}</Typography>
        {descricao && <Typography sx={{ mt: 1.5, fontSize: { xs: 14, md: 16 }, color: "#4b5563" }}>{descricao}</Typography>}
    </Box>
);

const ApresentacaoClarea = () => {
    const navigate = useNavigate();
    const { user } = useUser();
    const videoRef = useRef(null);
    const agendaRef = useRef(null);

    useEffect(() => {
        const script = document.createElement("script");
        script.src = "https://assets.calendly.com/assets/external/widget.js";
        script.async = true;
        document.body.appendChild(script);

        return () => {
            if (script.parentNode) script.parentNode.removeChild(script);
        };
    }, []);

    const rolarPara = (ref) => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });

    const primeiroNome = (user?.usuario?.Nome || "").trim().split(" ")[0];

    const passos = [
        {
            icone: <PlayCircleOutlineIcon />,
            titulo: "Assista à apresentação",
            descricao: "Conheça em poucos minutos como a Clarea Vital vai cuidar da contabilidade da sua empresa.",
            acao: "Assistir agora",
            onClick: () => rolarPara(videoRef),
        },
        {
            icone: <EventAvailableOutlinedIcon />,
            titulo: "Agende sua reunião de boas-vindas",
            descricao: "Escolha o melhor dia e horário para conversar com nosso time e alinhar os primeiros passos.",
            acao: "Agendar reunião",
            onClick: () => rolarPara(agendaRef),
        },
        {
            icone: <VerifiedUserOutlinedIcon />,
            titulo: "Envie seu certificado digital",
            descricao: "Com o certificado cadastrado, liberamos a emissão das suas notas fiscais pela plataforma.",
            acao: "Enviar certificado",
            onClick: () => navigate("../Dashboard/Certificado"),
        },
        {
            icone: <AssignmentIndOutlinedIcon />,
            titulo: "Complete sua ficha cadastral",
            descricao: "Seus dados completos permitem que a gente cuide de tudo sem precisar te pedir de novo.",
            acao: "Preencher ficha",
            onClick: () => navigate("../Dashboard/FichaCadastral"),
        },
    ];

    const recursos = [
        { icone: <ReceiptLongOutlinedIcon />, titulo: "Emissão de NFS-e", descricao: "Emita notas fiscais de serviço em poucos cliques." },
        { icone: <InsightsOutlinedIcon />, titulo: "Visão do faturamento", descricao: "Acompanhe o faturamento da sua empresa mês a mês." },
        { icone: <FolderOpenOutlinedIcon />, titulo: "Documentos organizados", descricao: "Envie e consulte seus documentos em um só lugar." },
        { icone: <SupportAgentOutlinedIcon />, titulo: "Contador ao seu lado", descricao: "Fale com o nosso time sempre que precisar." },
    ];

    return (
        <Box sx={{ backgroundColor: "#f4f6f8", minHeight: "100vh" }}>
            {/* Hero */}
            <Box
                sx={{
                    position: "relative",
                    overflow: "hidden",
                    background: `radial-gradient(circle at 85% 20%, rgba(255, 200, 69, 0.18), transparent 45%), radial-gradient(circle at 10% 90%, rgba(255, 255, 255, 0.06), transparent 40%), ${AZUL}`,
                    color: "#fff",
                    px: { xs: 2, md: "5%" },
                    pt: { xs: 3, md: 4 },
                    pb: { xs: 8, md: 12 },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                    <img src="/assets/logoClarea01.png" alt="Logo Clarea Vital" style={{ height: 44 }} />
                    <Button onClick={() => navigate("../Dashboard")} endIcon={<ArrowForwardIcon />} sx={{ textTransform: "none", fontSize: 13, fontWeight: 600, color: "#cbd5e1", "&:hover": { color: "#fff", backgroundColor: "rgba(255,255,255,0.06)" } }}>
                        Pular para a plataforma
                    </Button>
                </Box>

                <Box sx={{ maxWidth: 820, mx: "auto", mt: { xs: 6, md: 9 }, textAlign: "center" }}>
                    <motion.div variants={aparecer} initial="hidden" animate="visible" custom={0}>
                        <Chip
                            icon={<RocketLaunchOutlinedIcon sx={{ fontSize: "16px !important", color: `${AMARELO} !important` }} />}
                            label="Assinatura confirmada"
                            sx={{ backgroundColor: "rgba(255, 200, 69, 0.15)", color: AMARELO, fontWeight: 700, fontSize: 12, height: 28, letterSpacing: 0.4 }}
                        />
                    </motion.div>
                    <motion.div variants={aparecer} initial="hidden" animate="visible" custom={1}>
                        <Typography sx={{ mt: 2.5, fontSize: { xs: 30, sm: 40, md: 52 }, fontWeight: 800, lineHeight: 1.1 }}>
                            {primeiroNome ? <>Bem-vindo(a), <Box component="span" sx={{ color: AMARELO }}>{primeiroNome}</Box>!</> : <>Bem-vindo(a) à <Box component="span" sx={{ color: AMARELO }}>Clarea Vital</Box>!</>}
                        </Typography>
                    </motion.div>
                    <motion.div variants={aparecer} initial="hidden" animate="visible" custom={2}>
                        <Typography sx={{ mt: 2, fontSize: { xs: 15, md: 18 }, color: "#cbd5e1", maxWidth: 620, mx: "auto" }}>
                            A partir de agora, a contabilidade da sua empresa está em boas mãos. Separamos tudo o que você precisa para começar com o pé direito.
                        </Typography>
                    </motion.div>
                    {user?.plano?.nome && (
                        <motion.div variants={aparecer} initial="hidden" animate="visible" custom={3}>
                            <Box sx={{ mt: 3, display: "inline-flex", alignItems: "center", gap: 1.5, backgroundColor: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.1)", borderRadius: "12px", px: 2, py: 1 }}>
                                <WorkspacePremiumIcon sx={{ color: AMARELO, fontSize: 22 }} />
                                <Typography sx={{ fontSize: 14, color: "#e2e8f0" }}>
                                    Seu plano: <Box component="span" sx={{ fontWeight: 700, color: "#fff" }}>{user.plano.nome}</Box>
                                </Typography>
                            </Box>
                        </motion.div>
                    )}
                    <motion.div variants={aparecer} initial="hidden" animate="visible" custom={4}>
                        <Box sx={{ mt: 4, display: "flex", gap: 1.5, justifyContent: "center", flexWrap: "wrap" }}>
                            <Button startIcon={<PlayCircleOutlineIcon />} onClick={() => rolarPara(videoRef)} sx={botaoPrimarioSx}>
                                Assistir apresentação
                            </Button>
                            <Button startIcon={<CalendarMonthOutlinedIcon />} onClick={() => rolarPara(agendaRef)} sx={botaoSecundarioEscuroSx}>
                                Agendar reunião
                            </Button>
                        </Box>
                    </motion.div>
                </Box>
            </Box>

            <Box sx={{ px: { xs: 2, md: "5%" }, pb: 6 }}>
                {/* Vídeo */}
                <Box ref={videoRef} sx={{ maxWidth: 960, mx: "auto", mt: { xs: -5, md: -8 }, position: "relative", scrollMarginTop: 24 }}>
                    <Secao>
                        <Box sx={{ ...cardSx, p: { xs: 1, md: 1.5 }, boxShadow: "0 20px 50px rgba(11, 36, 61, 0.18)" }}>
                            <Box sx={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden", borderRadius: "12px", backgroundColor: "#000" }}>
                                <video
                                    src="/assets/VideoApresentacao.mp4"
                                    title="Apresentação Clarea Vital"
                                    controls
                                    playsInline
                                    preload="metadata"
                                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                                />
                            </Box>
                        </Box>
                    </Secao>
                </Box>

                {/* Próximos passos */}
                <Box sx={{ maxWidth: 1100, mx: "auto", mt: { xs: 7, md: 10 } }}>
                    <Secao>
                        <TituloSecao
                            etiqueta="Primeiros passos"
                            titulo="Seu roteiro para começar"
                            descricao="Quatro passos simples para deixar tudo pronto. Você pode fazer no seu ritmo — eles também ficam disponíveis na plataforma."
                        />
                    </Secao>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }, gap: 2 }}>
                        {passos.map((passo, i) => (
                            <Secao key={passo.titulo} custom={i} style={{ display: "flex" }}>
                                <Box
                                    sx={{
                                        ...cardSx,
                                        p: 3,
                                        width: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        position: "relative",
                                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                                        "&:hover": { transform: "translateY(-3px)", boxShadow: "0 12px 28px rgba(0,0,0,0.08)" },
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                        <Box sx={{ width: 44, height: 44, borderRadius: "12px", backgroundColor: AZUL, color: AMARELO, display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 24 } }}>
                                            {passo.icone}
                                        </Box>
                                        <Typography sx={{ fontSize: 36, fontWeight: 800, color: "#f3f4f6", lineHeight: 1 }}>{String(i + 1).padStart(2, "0")}</Typography>
                                    </Box>
                                    <Typography sx={{ mt: 2, fontSize: 16, fontWeight: 700, color: "#111827" }}>{passo.titulo}</Typography>
                                    <Typography sx={{ mt: 0.75, fontSize: 13, color: "#6b7280", flex: 1 }}>{passo.descricao}</Typography>
                                    <Button
                                        onClick={passo.onClick}
                                        endIcon={<ArrowForwardIcon sx={{ fontSize: "16px !important" }} />}
                                        sx={{ mt: 2, alignSelf: "flex-start", textTransform: "none", fontSize: 13, fontWeight: 700, color: "#111827", p: 0, minWidth: 0, "&:hover": { backgroundColor: "transparent", color: "#b45309" } }}
                                    >
                                        {passo.acao}
                                    </Button>
                                </Box>
                            </Secao>
                        ))}
                    </Box>
                </Box>

                {/* Agenda */}
                <Box ref={agendaRef} sx={{ maxWidth: 1100, mx: "auto", mt: { xs: 7, md: 10 }, scrollMarginTop: 24 }}>
                    <Secao>
                        <TituloSecao
                            etiqueta="Reunião de boas-vindas"
                            titulo="Vamos conversar?"
                            descricao="Escolha o dia e horário mais convenientes para você. Na reunião, alinhamos os próximos passos e tiramos todas as suas dúvidas."
                        />
                        <Box sx={{ ...cardSx, p: { xs: 0.5, md: 1 }, overflow: "hidden" }}>
                            <div
                                className="calendly-inline-widget"
                                data-url="https://calendly.com/personal-contblack/primeiros-passos-contblack-clone?text_color=11305e&primary_color=dcca30"
                                style={{ minWidth: "320px", height: "700px" }}
                            ></div>
                        </Box>
                    </Secao>
                </Box>

                {/* Recursos da plataforma */}
                <Box sx={{ maxWidth: 1100, mx: "auto", mt: { xs: 7, md: 10 } }}>
                    <Secao>
                        <TituloSecao
                            etiqueta="Sua plataforma"
                            titulo="Tudo da sua empresa em um só lugar"
                        />
                    </Secao>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }, gap: 2 }}>
                        {recursos.map((recurso, i) => (
                            <Secao key={recurso.titulo} custom={i}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, p: 2 }}>
                                    <Box sx={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: "#fef3c7", color: "#b45309", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, "& svg": { fontSize: 22 } }}>
                                        {recurso.icone}
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>{recurso.titulo}</Typography>
                                        <Typography sx={{ mt: 0.25, fontSize: 13, color: "#6b7280" }}>{recurso.descricao}</Typography>
                                    </Box>
                                </Box>
                            </Secao>
                        ))}
                    </Box>
                </Box>

                {/* CTA final */}
                <Box sx={{ maxWidth: 1100, mx: "auto", mt: { xs: 7, md: 10 } }}>
                    <Secao>
                        <Box
                            sx={{
                                borderRadius: "20px",
                                background: `radial-gradient(circle at 90% 10%, rgba(255, 200, 69, 0.2), transparent 50%), ${AZUL}`,
                                color: "#fff",
                                p: { xs: 3, md: 6 },
                                display: "flex",
                                flexDirection: { xs: "column", md: "row" },
                                alignItems: { xs: "flex-start", md: "center" },
                                justifyContent: "space-between",
                                gap: 3,
                            }}
                        >
                            <Box>
                                <Typography sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 800, lineHeight: 1.2 }}>
                                    Tudo pronto para começar?
                                </Typography>
                                <Typography sx={{ mt: 1, fontSize: { xs: 14, md: 16 }, color: "#cbd5e1", maxWidth: 520 }}>
                                    Acesse a plataforma para acompanhar sua empresa, emitir notas e falar com o nosso time.
                                </Typography>
                            </Box>
                            <Button endIcon={<ArrowForwardIcon />} onClick={() => navigate("../Dashboard")} sx={{ ...botaoPrimarioSx, flexShrink: 0, width: { xs: "100%", md: "auto" } }}>
                                Ir para a plataforma
                            </Button>
                        </Box>
                    </Secao>
                </Box>
            </Box>
        </Box>
    );
};

export default ApresentacaoClarea;
